<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center">
        <b-card-title class="mb-0">{{ $t('message.permissions_page_title') }}</b-card-title>
        <b-button variant="outline-grey" size="sm" :disabled="isLoading" @click="toggleAll">
          <feather-icon
            :icon="allExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="14"
            class="mr-25"
          />
          {{ allExpanded ? $t('message.collapse_all') : $t('message.expand_all') }}
        </b-button>
      </div>
      <b-alert
        variant="primary"
        show
        class="mb-0 mt-1"
      >
        <div class="p-1 d-flex align-items-start">
          <feather-icon
            icon="InfoIcon"
            size="18"
            class="mr-50 flex-shrink-0"
          />
          <span class="small">{{ $t('message.permissions_page_info') }}</span>
        </div>
      </b-alert>
    </b-card>

    <div v-if="isLoading" class="text-center py-3">
      <b-spinner variant="primary" />
    </div>

    <template v-else>
      <b-card v-for="group in groups" :key="group.headerKey" no-body>
        <div class="d-flex align-items-center px-2 py-1" style="cursor: pointer;" @click="expanded[group.headerKey] = !expanded[group.headerKey]">
          <feather-icon :icon="expanded[group.headerKey] ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" class="mr-50" />
          <h5 class="mb-0">{{ $t(group.headerKey) }}</h5>
          <span class="text-muted small ml-50">({{ group.rows.length }})</span>
        </div>

        <b-collapse v-model="expanded[group.headerKey]">
          <b-table-simple hover responsive small class="mb-0 permission-matrix">
            <b-thead head-variant="light">
              <b-tr>
                <b-th>{{ $t('message.tableHeader.module') }}</b-th>
                <b-th v-for="privilege in privilegeOrder" :key="privilege" class="text-center">
                  {{ privilegeLabel(privilege) }}
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="row in group.rows" :key="row.moduleKey">
                <b-td>
                  <div class="module-name">
                    <span v-if="row.moduleNavKey.parentKey">{{ $t(row.moduleNavKey.parentKey) }} — </span>
                    <span>{{ $t(row.moduleNavKey.key) }}</span>
                    <feather-icon
                      v-if="moduleNotes(row.moduleKey).length"
                      :id="`module-note-icon-${row.moduleKey}`"
                      icon="InfoIcon"
                      size="14"
                      class="text-warning ml-50 module-note-icon"
                    />
                    <b-popover
                      v-if="moduleNotes(row.moduleKey).length"
                      :target="`module-note-icon-${row.moduleKey}`"
                      triggers="click"
                      placement="top"
                      custom-class="module-note-popover"
                    >
                      <p
                        v-for="(note, i) in moduleNotes(row.moduleKey)"
                        :key="i"
                        class="mb-0 small"
                      >
                        {{ note }}
                      </p>
                    </b-popover>
                  </div>
                  <div class="small text-primary">{{ row.moduleKey }}</div>
                </b-td>
                <b-td v-for="(cell, index) in row.cells" :key="index">
                  <div class="permission-grid">
                    <b-form-checkbox
                      :checked="viewMode(cell) === 'all'"
                      @change="setViewMode(privilegeOrder[index], row.moduleKey, viewMode(cell) === 'all' ? null : 'all')"
                      class="mb-0"
                    >
                      <span class="small">{{ $t('message.permission_view_all') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="viewMode(cell) === 'own'"
                      @change="setViewMode(privilegeOrder[index], row.moduleKey, viewMode(cell) === 'own' ? null : 'own')"
                      class="mb-0"
                    >
                      <span class="small">{{ $t('message.permission_view_own') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(cell.edit && cell.edit.allowed)"
                      @change="toggleAction(privilegeOrder[index], row.moduleKey, 'edit', cell)"
                      class="mb-0"
                    >
                      <span class="small">{{ $t('message.permission_action_edit') }}</span>
                      <span v-if="cell.edit && cell.edit.allowed && viewMode(cell) === 'own'" class="text-muted" style="font-size: 10px;">
                        ({{ $t('message.permission_scope_own') }})
                      </span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(cell.create && cell.create.allowed)"
                      @change="toggleAction(privilegeOrder[index], row.moduleKey, 'create', cell)"
                      class="mb-0"
                    >
                      <span class="small">{{ $t('message.permission_action_create') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(cell.export && cell.export.allowed)"
                      @change="toggleAction(privilegeOrder[index], row.moduleKey, 'export', cell)"
                      class="mb-0"
                    >
                      <span class="small">{{ $t('message.permission_action_export') }}</span>
                    </b-form-checkbox>
                    <b-form-checkbox
                      :checked="!!(cell.delete && cell.delete.allowed)"
                      @change="toggleAction(privilegeOrder[index], row.moduleKey, 'delete', cell)"
                      class="mb-0"
                    >
                      <span class="small">{{ $t('message.permission_action_delete') }}</span>
                    </b-form-checkbox>
                  </div>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-collapse>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BButton, BCollapse, BTableSimple, BThead, BTbody, BTr, BTh, BTd,
  BFormCheckbox, BSpinner, BPopover, BAlert,
} from 'bootstrap-vue'
import { onMounted } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import MODULE_ACTION_NOTES from '@/composables/permissionModuleNotes'
import usePermissionsList from './usePermissionsList'

export default {
  components: {
    BCard,
    BCardTitle,
    BButton,
    BCollapse,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BFormCheckbox,
    BSpinner,
    BPopover,
    BAlert,
  },
  setup() {
    const {
      isLoading, privilegeOrder, groups, expanded, allExpanded, toggleAll, fetchPermissions,
      viewMode, setViewMode, toggleAction,
    } = usePermissionsList()

    onMounted(() => {
      fetchPermissions()
    })

    // 'consultant' shows as "Admin"/"Administrador" on this screen too now (own key, kept
    // separate from userRoles.consultant on purpose — used elsewhere, e.g. the worker
    // privilege dropdown — so the two screens' wording can diverge again later if needed).
    const privilegeLabel = privilege => (
      privilege === 'consultant'
        ? i18n.t('message.permission_privilege_consultant')
        : i18n.t(`userRoles.${privilege}`)
    )

    const moduleNotes = moduleKey => MODULE_ACTION_NOTES[moduleKey] || []

    return {
      isLoading,
      privilegeOrder,
      groups,
      expanded,
      allExpanded,
      toggleAll,
      viewMode,
      privilegeLabel,
      moduleNotes,
      setViewMode,
      toggleAction,
    }
  },
}
</script>

<style scoped>
.permission-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 10px;
}
/* Left sub-column of each privilege cell (view_all, edit, export — the odd
   DOM children, since the grid fills row-major) sits right next to the
   divider from the previous column; nudge it away a bit. */
.permission-grid > .custom-control:nth-child(odd) {
  margin-left: 6px;
}
.module-name {
  font-weight: 500;
}
.module-note-icon {
  cursor: pointer;
  vertical-align: -2px;
}
.permission-matrix td {
  vertical-align: top;
}
/* `responsive` makes b-table-simple render a `div.table-responsive` wrapper as its
   root, with the actual <table> as a child — so `.permission-matrix` (applied via the
   `class` prop) lands on that wrapper via Vue's scoped-CSS root-node rule, not on the
   table itself. Locking min-width there just blows the wrapper past its container with
   nothing to scroll it. The lock needs to target the inner <table> instead, hence ::v-deep. */
.permission-matrix ::v-deep .table {
  min-width: 1520px;
}

/* Stays fluid (wraps text, no scroll) above 1520px of available width; below that,
   the table can't shrink further and the responsive wrapper (`.permission-matrix` itself)
   scrolls horizontally instead. */
.permission-matrix {
  overflow-x: auto;
}
.permission-matrix th:first-child,
.permission-matrix td:first-child {
  padding-left: 1.5rem;
  width: 280px;
  max-width: 280px;
  word-break: break-word;
}

/* Vertical divider between the module column and each privilege column */
.permission-matrix th,
.permission-matrix td {
  border-right: 1px solid #e9ecef;
}
.permission-matrix th:last-child,
.permission-matrix td:last-child {
  border-right: none;
}

/* Alternating background per privilege column — reads better than a heavier
   divider line since it separates columns along their full height (header
   included) rather than just at the edges. */
.permission-matrix th:nth-child(even):not(:first-child),
.permission-matrix td:nth-child(even):not(:first-child) {
  background-color: #EAEEF5;
}

/* Module column stays fixed while scrolling horizontally — desktop only */
@media (min-width: 768px) {
  .permission-matrix th:first-child,
  .permission-matrix td:first-child {
    position: sticky;
    left: 0;
    z-index: 2;
  }
  .permission-matrix td:first-child {
    background-color: #fff;
  }
  .permission-matrix th:first-child {
    background-color: #f3f2f7;
  }
}
</style>
